/**
 * Standalone AI session generation for a given client.
 * Supports: initial generation, regeneration with style/variation, and auto-completion.
 * Used by auto-generation (App.jsx) and UI (ClientProfileView).
 */

import { callAI } from "./api";
import { buildEquipmentSummary, buildFallbackExercises } from "./ai";
import { buildClientHistorySummary, buildCrossClientSummary } from "./clientStats";
import { buildPreferencesBlock } from "./aiPreferences";
import { buildSystemPrompt, buildMaturityBlock, sanitizeForPrompt, validateClientFields, moderateOutput } from "./aiSystem";
import { track, EVENTS } from "./analytics";
import { logger, toLogError } from "../utils/logger";

const EXERCISE_SCHEMA = {
  type: "object",
  properties: {
    name: { type: "string" },
    sets: { type: "number" },
    reps: { type: "number" },
    weight: { type: "number" },
    rest: { type: "number" },
    mode: { type: "string" },
    duration: { type: "number" },
    youtubeQuery: { type: "string" },
    notes: { type: "string" },
  },
  required: ["name", "sets", "rest", "mode"],
};

const TOOL_SESSION = {
  name: "create_session",
  description: "Génère une séance structurée pour un athlète AthleOS.",
  input_schema: {
    type: "object",
    properties: {
      title: { type: "string" },
      focus: { type: "string" },
      duration: { type: "number" },
      warmup: { type: "string" },
      exercises: { type: "array", minItems: 4, items: EXERCISE_SCHEMA },
      cooldown: { type: "string" },
      coachNote: { type: "string" },
    },
    required: ["title", "focus", "duration", "warmup", "exercises", "cooldown", "coachNote"],
    additionalProperties: true,
  },
};

const TOOL_COMPLETE = {
  name: "complete_exercises",
  description: "Complète une séance existante avec une liste cohérente de 8 à 10 exercices principaux.",
  input_schema: {
    type: "object",
    properties: {
      exercises: { type: "array", minItems: 8, items: EXERCISE_SCHEMA },
    },
    required: ["exercises"],
    additionalProperties: false,
  },
};

const REGEN_MODES = {
  light: "Variante légère de la séance précédente : même durée et même structure (nombre de blocs), environ 30–40 % des exercices changent.",
  harder: "Séance légèrement plus intense : même durée et même structure, mais volume ou difficulté légèrement augmentés.",
  easier: "Séance plus douce / de récupération : même durée globale mais intensité réduite, davantage de mobilité et de récupération.",
  upper: "Mettre davantage l'accent sur le haut du corps (sans totalement négliger le reste).",
  lower: "Mettre davantage l'accent sur le bas du corps (sans totalement négliger le reste).",
  core: "Mettre davantage l'accent sur le gainage et la mobilité.",
};

function buildContextBlocks(client, clients, equipment) {
  const recentHist = !client.sessions.length
    ? "Aucune séance."
    : client.sessions.slice(-2).map(s =>
        `${s.date}:\n${s.exercises?.map(e =>
          `- ${e.name}: ${e.sets}x${e.reps || e.duration + "s"} ${e.weight ? `@ ${e.weight}kg` : ""}`
        ).join("\n") || ""}`
      ).join("\n\n");

  const consolidatedStats = buildClientHistorySummary(client);
  const crossClientBlock = buildCrossClientSummary(clients, client);
  const tagsBlock = (client.tags || []).length ? `\n- Tags coach: ${client.tags.join(", ")}` : "";
  const recentNotes = (client.notes || []).slice(-3);
  const notesBlock = recentNotes.length
    ? `\nNotes du coach (les plus récentes):\n${recentNotes.map(n => `- [${n.date}] ${sanitizeForPrompt(n.text)}`).join("\n")}`
    : "";
  const prefsBlock = buildPreferencesBlock(client);
  const maturityBlock = buildMaturityBlock(client);
  const eqSummary = buildEquipmentSummary(equipment);

  return { recentHist, consolidatedStats, crossClientBlock, tagsBlock, notesBlock, prefsBlock, maturityBlock, eqSummary };
}

function buildVariationBlock(previousSession, regenStyle) {
  if (!previousSession || !regenStyle || !REGEN_MODES[regenStyle]) return "";
  const prevSummary = `Titre: ${previousSession.title}\nFocus: ${previousSession.focus}\nExercices:\n${
    (previousSession.exercises || []).map((e, idx) =>
      `${idx + 1}. ${e.name} – ${e.sets || 3}x${e.reps || e.duration || ""}${e.weight ? ` @${e.weight}kg` : ""}`
    ).join("\n")
  }`;
  return `\n\nSéance précédente proposée:\n${prevSummary}\n\nVariation demandée par le coach:\n${REGEN_MODES[regenStyle]}`;
}

function buildMainPrompt(client, ctx, variationBlock) {
  return `Profil du client:
- Âge: ${client.age || "non renseigné"}
- Niveau: ${client.niveau || "non renseigné"}
- OBJECTIF PRINCIPAL (North Star): ${sanitizeForPrompt(client.objective) || "non renseigné"}
- Contraintes physiques: ${sanitizeForPrompt(client.constraints) || "aucune"}
- Préférences: ${sanitizeForPrompt(client.preferences) || "aucune"}${ctx.tagsBlock}

${ctx.maturityBlock}

${ctx.consolidatedStats}

Détail des 2 dernières séances:
${ctx.recentHist}
${ctx.notesBlock}
${ctx.crossClientBlock ? `\n${ctx.crossClientBlock}` : ""}
${ctx.prefsBlock ? `\n${ctx.prefsBlock}` : ""}

Matériel disponible: ${ctx.eqSummary.available}
Matériel indisponible: ${ctx.eqSummary.unavailable}

Tâche:
En respectant le schéma de l'outil "create_session", génère UNE séance complète et réaliste (échauffement, blocs de travail, finisher/retour au calme) qui:
- sert DIRECTEMENT l'objectif principal du client (chaque exercice doit y contribuer),
- respecte les contraintes physiques (aucun mouvement dangereux pour les zones fragiles),
- est adaptée à la PHASE de la relation (voir ci-dessus),
- adapte volume/intensité au niveau,
- exploite l'historique consolidé pour faire progresser le client,
- tient compte de la charge récente (éviter de surcharger une zone déjà très sollicitée),
- intègre les tags du coach (ex: si "Blessé" ou "En reprise", réduire l'intensité et éviter les mouvements à risque),
- prend en compte les notes du coach pour personnaliser la séance,
- s'inspire des exercices qui fonctionnent bien chez les clients similaires (si disponible),
- tient compte des corrections passées du coach,
- privilégie les exercices compatibles avec le matériel disponible (variante poids du corps quand c'est possible),
- reste exécutable sur le terrain et lisible en un coup d'œil.

Contraintes supplémentaires:
- Durée totale visée: 60 à 70 minutes.
- Le tableau "exercises" doit contenir entre 8 et 10 exercices (jamais moins de 8).
- Organiser en 2 à 3 blocs/circuits distincts (2 à 4 exercices par bloc, préciser le bloc dans "notes").
- Répartir intelligemment le temps: échauffement 5–10 min, 2–3 blocs de travail, puis finisher/retour au calme.${variationBlock}`;
}

async function completeExercises(base, client, ctx, coachProfile) {
  if (base.exercises.length >= 8) return base;
  try {
    const completionPrompt = `Complète la séance suivante en proposant une liste cohérente de 8 à 10 exercices principaux pour le tableau "exercises" (jamais moins de 8).

Contexte:
- OBJECTIF PRINCIPAL: ${sanitizeForPrompt(client.objective)}
- Contraintes: ${sanitizeForPrompt(client.constraints)}
- Niveau: ${client.niveau}${ctx.tagsBlock}
${ctx.notesBlock}
${ctx.prefsBlock ? `\n${ctx.prefsBlock}` : ""}

Séance actuelle:
- Titre: ${base.title}
- Focus: ${base.focus}
- Durée: ${base.duration} min

Règles:
- Chaque exercice doit servir l'objectif principal.
- Garde la cohérence avec le titre, le focus, la durée et les contraintes.
- Organise les exercices en bloc(s)/circuit(s).
- Tiens compte des corrections passées du coach.
- Génère UNIQUEMENT la valeur du champ "exercises" via l'outil "complete_exercises".

Matériel disponible: ${ctx.eqSummary.available}
Matériel indisponible: ${ctx.eqSummary.unavailable}`;

    const d2 = await callAI({
      _action: "session_gen",
      system: buildSystemPrompt(coachProfile),
      temperature: 0.7,
      maxTokens: 900,
      tools: [TOOL_COMPLETE],
      toolChoice: { type: "tool", name: "complete_exercises" },
      messages: [{ role: "user", content: [{ type: "text", text: completionPrompt }] }],
    });
    const t2 = (d2.content || []).find(c => c.type === "tool_use" && c.name === "complete_exercises");
    const ex2 = t2?.input?.exercises;
    if (Array.isArray(ex2) && ex2.length >= 8) return { ...base, exercises: ex2 };
  } catch (e) {
    logger.error("[AI] Completion failed", toLogError(e));
    track(EVENTS.AI_ERROR, { context: "completion", error: e?.message || String(e) });
  }
  return base;
}

function validateSessionSchema(session) {
  if (!session || typeof session !== "object") return { ok: false, reason: "not_object" };
  if (typeof session.title !== "string" || !session.title.trim()) return { ok: false, reason: "missing_title" };
  if (typeof session.focus !== "string") return { ok: false, reason: "missing_focus" };
  if (typeof session.duration !== "number" || session.duration <= 0) return { ok: false, reason: "invalid_duration" };
  if (!Array.isArray(session.exercises)) return { ok: false, reason: "exercises_not_array" };
  for (let i = 0; i < session.exercises.length; i++) {
    const ex = session.exercises[i];
    if (!ex || typeof ex !== "object") return { ok: false, reason: `exercise_${i}_not_object` };
    if (typeof ex.name !== "string" || !ex.name.trim()) return { ok: false, reason: `exercise_${i}_missing_name` };
    if (typeof ex.sets !== "number" || ex.sets <= 0) return { ok: false, reason: `exercise_${i}_invalid_sets` };
    if (typeof ex.mode !== "string") return { ok: false, reason: `exercise_${i}_missing_mode` };
  }
  return { ok: true };
}

/**
 * Main generation function.
 * @param {Object} client - Client object
 * @param {Array} clients - All clients (for cross-client)
 * @param {Object} equipment - Equipment config
 * @param {string} coachProfile - Coach profile text
 * @param {Object} [options] - Optional: { previousSession, regenStyle }
 * @returns {Object|null} Generated session or null
 */
export async function generateSessionForClient(client, clients, equipment, coachProfile, options = {}) {
  const v = validateClientFields(client);
  if (!v.ok) {
    logger.warn("[AI] Validation failed for client fields", { validation: v });
    return null;
  }

  const ctx = buildContextBlocks(client, clients, equipment);
  const variationBlock = buildVariationBlock(options.previousSession, options.regenStyle);
  const prompt = buildMainPrompt(client, ctx, variationBlock);

  const d = await callAI({
    _action: "session_gen",
    system: buildSystemPrompt(coachProfile),
    temperature: 0.7,
    maxTokens: 1400,
    tools: [TOOL_SESSION],
    toolChoice: { type: "tool", name: "create_session" },
    messages: [{ role: "user", content: [{ type: "text", text: prompt }] }],
  });

  const toolMsg = (d.content || []).find(c => c.type === "tool_use" && c.name === "create_session");
  if (!toolMsg || !toolMsg.input) {
    logger.warn("[AI] No tool_use response from create_session");
    track(EVENTS.AI_ERROR, { context: "session_generation", error: "no_tool_use_response" });
    return null;
  }

  let session = toolMsg.input;

  const schemaCheck = validateSessionSchema(session);
  if (!schemaCheck.ok) {
    logger.warn("[AI] Session schema invalid", { reason: schemaCheck.reason });
    track(EVENTS.AI_ERROR, { context: "session_schema", error: schemaCheck.reason });
    session = {
      title: session.title || `Séance ${client.niveau || ""}`.trim(),
      focus: session.focus || "Général",
      duration: typeof session.duration === "number" ? session.duration : 60,
      warmup: session.warmup || "Échauffement articulaire 5 min",
      exercises: Array.isArray(session.exercises) ? session.exercises : [],
      cooldown: session.cooldown || "Étirements 5 min",
      coachNote: session.coachNote || "",
    };
  }

  session = await completeExercises(session, client, ctx, coachProfile);

  if (!session.exercises || !session.exercises.length) {
    logger.warn("[AI] No exercises after completion, using fallback");
    track(EVENTS.AI_ERROR, { context: "session_fallback", error: "no_exercises" });
    session = { ...session, exercises: buildFallbackExercises() };
  }

  if (session.coachNote) {
    const mod = moderateOutput(session.coachNote);
    if (!mod.ok) session.coachNote = "";
  }

  return session;
}
